import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import { calculateTokenDelay, mergeTimingConfig } from './timing';
import { getVisibleText, tokenizeText } from './tokenizer';
import {
    StreamingToken,
    UseStreamingTextProps,
    UseStreamingTextReturn,
} from './types';

export { DEFAULT_TIMING_CONFIG } from './timing';
export type {
    StreamingTimingConfig,
    StreamingToken,
    UseStreamingTextProps,
    UseStreamingTextReturn,
} from './types';

/**
 * Hook for streaming text animation with word-by-word reveal.
 * Provides ChatGPT-like fade-in effect with variable timing.
 *
 * ## Architecture Overview
 *
 * This hook uses a ref-based callback pattern to avoid two common React pitfalls:
 *
 * 1. **Stale Closures**: If callbacks were in useEffect dependencies, changing them
 *    would restart the animation. Instead, we store callbacks in refs and update
 *    them via separate useEffects, so the animation loop always reads fresh values.
 *
 * 2. **Unnecessary Re-renders**: The animation loop modifies token visibility via
 *    setState, but reads from `initialTokensRef` (not the tokens state). This
 *    prevents the effect from depending on state it modifies, avoiding infinite loops.
 *
 * ## Key State Management
 *
 * - `tokens` (state): The renderable token array with visibility flags
 * - `initialTokensRef` (ref): Stable reference to tokens for the animation loop
 * - `readyToAnimate` (counter via useReducer): Increments on each text change to
 *   guarantee the animation effect re-runs. Token state updates don't change the
 *   counter, so they won't restart the animation mid-flight.
 * - `hasStartedRef` (ref): Ensures animation only starts once per text change
 * - `cancelRef` (ref): Reset to `false` at the start of each new animation to
 *   prevent stale `true` values left by the prior animation effect's cleanup.
 *
 * ## Animation Flow
 *
 * 1. Text changes → tokenize → reset all state → increment readyToAnimate counter
 * 2. Re-render with new counter → old animation effect cleanup (sets cancelRef=true)
 *    → new animation effect resets cancelRef and starts
 * 3. Recursive setTimeout reveals tokens one-by-one with calculated delays
 * 4. Each reveal updates tokens state (triggers re-render for UI)
 * 5. Animation completes → call onComplete callback
 *
 * If `timing.maxTypingDurationMs` is set, a parallel timer short-circuits step 3
 * once the cap elapses: the remaining tokens are revealed together,
 * `bulkRevealStartIndex` is set to the first of them (so consumers can fade that
 * block in at their own duration), and step 5 runs immediately.
 *
 * @example
 * ```tsx
 * const { tokens, isAnimating, isComplete } = useStreamingText({
 *   text: "Hello world",
 *   onComplete: () => console.log("Done!"),
 *   timing: { baseDelayMs: 50 },
 * });
 * ```
 */
export function useStreamingText({
    text,
    autoStart = true,
    timing: timingOverrides,
    variant,
    onComplete,
    onStart,
    onTokenReveal,
    disableAnimation = false,
}: UseStreamingTextProps): UseStreamingTextReturn {
    // Unique ID prefix for token IDs to avoid clashes between multiple instances
    const idPrefix = useId();

    // Memoize timing config to maintain stable reference
    const timing = useMemo(
        () => mergeTimingConfig(timingOverrides, variant),
        [timingOverrides, variant]
    );

    const [tokens, setTokens] = useState<StreamingToken[]>(() => {
        const newTokens = tokenizeText(text, idPrefix);
        if (disableAnimation) {
            return newTokens.map((t) => ({ ...t, isVisible: true }));
        }
        return newTokens;
    });
    const [isAnimating, setIsAnimating] = useState(false);
    const [isComplete, setIsComplete] = useState(disableAnimation);
    // Index of the first token revealed by the `maxTypingDurationMs` cap, so
    // consumers can fade that block in at a different duration than the
    // word-by-word tokens. Stays null when the cap is unset or never reached.
    const [bulkRevealStartIndex, setBulkRevealStartIndex] = useState<
        number | null
    >(null);

    // Counter that increments on each text change, ensuring the animation effect
    // always re-runs. A boolean would fail to trigger a re-run when the value is
    // already true, preventing the animation from restarting after a text change.
    const [readyToAnimate, dispatchReady] = useReducer((s: number) => s + 1, 0);

    // Refs for callbacks: stored in refs so animation loop always has fresh values
    // without needing them in useEffect dependencies (which would restart animation).
    const onCompleteRef = useRef(onComplete);
    const onStartRef = useRef(onStart);
    const onTokenRevealRef = useRef(onTokenReveal);
    const timingRef = useRef(timing);

    // Stable token reference for animation loop. The loop reads from this ref
    // (not tokens state) to avoid depending on state it modifies. This prevents
    // the useEffect from re-running on every token visibility update.
    const initialTokensRef = useRef<StreamingToken[]>(tokens);

    // Keep refs updated with latest values
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        onStartRef.current = onStart;
    }, [onStart]);

    useEffect(() => {
        onTokenRevealRef.current = onTokenReveal;
    }, [onTokenReveal]);

    useEffect(() => {
        timingRef.current = timing;
    }, [timing]);

    const cancelRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    );
    const bulkRevealTimeoutRef = useRef<
        ReturnType<typeof setTimeout> | undefined
    >(undefined);
    const hasStartedRef = useRef(false);
    const currentIndexRef = useRef(-1);

    // Reset when text changes
    useEffect(() => {
        cancelRef.current = true;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (bulkRevealTimeoutRef.current) {
            clearTimeout(bulkRevealTimeoutRef.current);
        }

        const newTokens = tokenizeText(text, idPrefix);
        initialTokensRef.current = newTokens;

        if (disableAnimation) {
            // When animation is disabled, show all tokens immediately
            setTokens(newTokens.map((t) => ({ ...t, isVisible: true })));
            setIsComplete(true);
        } else {
            setTokens(newTokens);
            setIsComplete(false);
        }

        setIsAnimating(false);
        setBulkRevealStartIndex(null);
        cancelRef.current = false;
        hasStartedRef.current = false;
        currentIndexRef.current = -1;
        dispatchReady();
    }, [text, disableAnimation, idPrefix]);

    // Animation loop effect. Intentionally depends ONLY on autoStart and readyToAnimate.
    // Does NOT depend on tokens, callbacks, or timing (accessed via refs instead).
    // This ensures the animation runs once per text change without restarting.
    useEffect(() => {
        const initialTokens = initialTokensRef.current;

        if (
            !autoStart ||
            !readyToAnimate ||
            hasStartedRef.current ||
            initialTokens.length === 0 ||
            disableAnimation
        ) {
            return;
        }

        cancelRef.current = false;
        hasStartedRef.current = true;
        setIsAnimating(true);
        onStartRef.current?.();

        const revealToken = (index: number) => {
            if (cancelRef.current) {
                return;
            }

            if (index >= initialTokens.length) {
                // Finished naturally, so the cap below is moot
                clearTimeout(bulkRevealTimeoutRef.current);
                setIsAnimating(false);
                setIsComplete(true);
                onCompleteRef.current?.();
                return;
            }

            const currentToken = initialTokens[index];
            const prevToken = index > 0 ? initialTokens[index - 1] : null;
            const delay = calculateTokenDelay(
                currentToken,
                prevToken,
                timingRef.current
            );

            timeoutRef.current = setTimeout(() => {
                if (cancelRef.current) {
                    return;
                }

                currentIndexRef.current = index;

                setTokens((prev) =>
                    prev.map((t, i) =>
                        i === index ? { ...t, isVisible: true } : t
                    )
                );

                onTokenRevealRef.current?.(currentToken);

                // Schedule next token
                revealToken(index + 1);
            }, delay);
        };

        // Cap the word-by-word phase: when the cap elapses, stop the per-token
        // loop and reveal whatever is left in one go so the whole reveal fits a
        // fixed time budget no matter how long the text is.
        const { maxTypingDurationMs } = timingRef.current;
        if (maxTypingDurationMs > 0) {
            bulkRevealTimeoutRef.current = setTimeout(() => {
                if (cancelRef.current) {
                    return;
                }

                // Stops the in-flight revealToken chain
                cancelRef.current = true;
                clearTimeout(timeoutRef.current);

                setBulkRevealStartIndex(currentIndexRef.current + 1);
                setTokens((prev) =>
                    prev.map((t) =>
                        t.isVisible ? t : { ...t, isVisible: true }
                    )
                );
                setIsAnimating(false);
                setIsComplete(true);
                onCompleteRef.current?.();
            }, maxTypingDurationMs);
        }

        // Start revealing from first token
        revealToken(0);

        return () => {
            cancelRef.current = true;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (bulkRevealTimeoutRef.current) {
                clearTimeout(bulkRevealTimeoutRef.current);
            }
        };
    }, [autoStart, readyToAnimate, disableAnimation]);

    const cancel = useCallback(() => {
        cancelRef.current = true;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (bulkRevealTimeoutRef.current) {
            clearTimeout(bulkRevealTimeoutRef.current);
        }
        setIsAnimating(false);
    }, []);

    const skipToEnd = useCallback(() => {
        cancel();
        setTokens((prev) => prev.map((t) => ({ ...t, isVisible: true })));
        setIsComplete(true);
        onCompleteRef.current?.();
    }, [cancel]);

    const restart = useCallback(() => {
        cancel();
        cancelRef.current = false;
        hasStartedRef.current = false;
        currentIndexRef.current = -1;
        const newTokens = tokenizeText(text, idPrefix);
        initialTokensRef.current = newTokens;
        setTokens(newTokens);
        setIsComplete(false);
        setBulkRevealStartIndex(null);
        dispatchReady();
    }, [cancel, text, idPrefix]);

    // Memoize visible text to avoid recalculating on every render
    const visibleText = useMemo(() => getVisibleText(tokens), [tokens]);

    return {
        tokens,
        isAnimating,
        isComplete,
        visibleText,
        fullText: text,
        bulkRevealStartIndex,
        cancel,
        skipToEnd,
        restart,
    };
}
